import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import ReceivingLabelPrint from '../ReceivingLabelPrint';
import './MaterialLabelPrinter.css';

/**
 * Material Label Printer
 * Print labels for raw materials already in inventory
 * Uses same 4x3 label format as goods receipt
 */
function MaterialLabelPrinter() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [labelCount, setLabelCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [recentPrints, setRecentPrints] = useState([]);

  const printRef = useRef();

  // Search for items
  const handleSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`/api/native-items?q=${encodeURIComponent(query)}&pageSize=20`);
      setSearchResults((response.data?.items || []).map(item => ({
        id: item.id,
        item_id: item.netsuiteId || item.sku,
        sku: item.sku,
        display_name: item.name,
        description: item.name
      })));
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    }
    setLoading(false);
  };

  // Debounced search
  const debounceRef = useRef(null);
  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(value), 300);
  };

  // Select item for printing
  const selectItem = (item) => {
    setSelectedItem({
      item_id: item.item_id || item.sku,
      sku: item.sku,
      display_name: item.display_name || item.description,
      description: item.description,
      vendor_name: item.vendor_name || ''
    });
    setSearchQuery('');
    setSearchResults([]);
  };

  // Print handler
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Label-${selectedItem?.sku || 'item'}`,
    onAfterPrint: () => {
      // Add to recent prints
      if (selectedItem) {
        setRecentPrints(prev => {
          const filtered = prev.filter(p => p.sku !== selectedItem.sku);
          return [{ ...selectedItem, printedAt: new Date() }, ...filtered].slice(0, 10);
        });
      }
    }
  });

  // Clear selection
  const clearSelection = () => {
    setSelectedItem(null);
    setLabelCount(1);
  };

  return (
    <div className="material-label-printer">
      <div className="mlp-header">
        <h1>Material Label Printer</h1>
        <p>Print labels for raw materials in inventory</p>
      </div>

      <div className="mlp-content">
        {/* Search Section */}
        <div className="mlp-search-section">
          <div className="mlp-search-box">
            <input
              type="text"
              value={searchQuery}
              onChange={onSearchChange}
              placeholder="Search by SKU, item ID, or description..."
              className="mlp-search-input"
              autoFocus
            />
            {loading && <div className="mlp-search-loading">Searching...</div>}
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mlp-search-results">
              {searchResults.map((item, idx) => (
                <div
                  key={item.id || idx}
                  className="mlp-search-item"
                  onClick={() => selectItem(item)}
                >
                  <div className="mlp-item-sku">{item.sku}</div>
                  <div className="mlp-item-desc">{item.display_name || item.description}</div>
                  {item.vendor_name && (
                    <div className="mlp-item-vendor">{item.vendor_name}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected Item / Print Section */}
        {selectedItem && (
          <div className="mlp-print-section">
            <div className="mlp-selected-item">
              <div className="mlp-selected-header">
                <h2>Selected Item</h2>
                <button onClick={clearSelection} className="mlp-clear-btn">Clear</button>
              </div>
              <div className="mlp-selected-details">
                <div className="mlp-detail-row">
                  <span className="mlp-detail-label">Item ID:</span>
                  <span className="mlp-detail-value">{selectedItem.item_id}</span>
                </div>
                <div className="mlp-detail-row">
                  <span className="mlp-detail-label">SKU:</span>
                  <span className="mlp-detail-value">{selectedItem.sku}</span>
                </div>
                <div className="mlp-detail-row">
                  <span className="mlp-detail-label">Description:</span>
                  <span className="mlp-detail-value">{selectedItem.display_name || selectedItem.description}</span>
                </div>
                {selectedItem.vendor_name && (
                  <div className="mlp-detail-row">
                    <span className="mlp-detail-label">Vendor:</span>
                    <span className="mlp-detail-value">{selectedItem.vendor_name}</span>
                  </div>
                )}
              </div>

              <div className="mlp-print-controls">
                <div className="mlp-label-count">
                  <label>Labels to print:</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={labelCount}
                    onChange={(e) => setLabelCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                    className="mlp-count-input"
                  />
                </div>
                <button onClick={handlePrint} className="mlp-print-btn">
                  Print {labelCount} Label{labelCount > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recent Prints */}
        {recentPrints.length > 0 && !selectedItem && (
          <div className="mlp-recent-section">
            <h3>Recent Prints</h3>
            <div className="mlp-recent-list">
              {recentPrints.map((item, idx) => (
                <div
                  key={idx}
                  className="mlp-recent-item"
                  onClick={() => selectItem(item)}
                >
                  <div className="mlp-recent-sku">{item.sku}</div>
                  <div className="mlp-recent-desc">{item.display_name || item.description}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!selectedItem && searchResults.length === 0 && !searchQuery && recentPrints.length === 0 && (
          <div className="mlp-empty-state">
            <p>Search for an item to print labels</p>
          </div>
        )}
      </div>

      {/* Hidden print component */}
      <div style={{ display: 'none' }}>
        <ReceivingLabelPrint
          ref={printRef}
          item={selectedItem}
          poNumber=""
          receivedDate={new Date()}
          quantity={labelCount}
        />
      </div>
    </div>
  );
}

export default MaterialLabelPrinter;
